<template>
  <v-snackbar
    v-model="showSnackbar"
    :timeout="-1"
    bottom
    color="info"
  >
    {{ $t("version_check.title") }}
    <template v-slot:action="{ attrs }">
      <v-btn
        v-bind="attrs"
        text
        @click="onUpdateClick"
      >
        {{ $t("version_check.update_now") }}
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script>
import { Capacitor } from "@capacitor/core";
import {
  onUpdateAvailableAction,
  onControllerChangeAction,
  onHiddenAction,
  resolveWorkerToSignal,
  FALLBACK_RELOAD_MS,
} from "@/helpers/swUpdate";

// PWA auto-update (пара к registerServiceWorker.js; решения — в helpers/swUpdate.js).
// Получает 'swUpdated', когда новый service worker установлен и ждёт активации:
//   - вкладка скрыта → тихо активируем (SKIP_WAITING) и перезагружаемся;
//   - вкладка активна → снекбар «Обновить», а если юзер уйдёт с вкладки — тихо.
// ВАЖНО: SKIP_WAITING действует на весь origin, controllerchange прилетает во ВСЕ
// вкладки. Перезагружаемся по нему только если вкладка скрыта или сама просила
// апдейт; чужая видимая вкладка получает снекбар (клик = reload) и не теряет форму.
// Только web: в нативных Capacitor-сборках обновления идут через сторы (AppVersionCheck).
export default {
  name: "AppUpdateNotifier",
  data() {
    return {
      showSnackbar: false,
      waitingWorker: null,
      updateRequested: false, // эта вкладка отправила SKIP_WAITING (клик или тихий путь)
      controllerChanged: false, // новый SW уже контролирует origin
      reloading: false,
      fallbackTimer: null, // страховочный reload, если controllerchange не пришёл
    };
  },
  mounted() {
    if (Capacitor.getPlatform() !== "web") return;

    document.addEventListener("swUpdated", this.onUpdateAvailable);
    document.addEventListener("visibilitychange", this.onVisibilityChange);

    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        this.onControllerChange
      );
    }
  },
  beforeDestroy() {
    document.removeEventListener("swUpdated", this.onUpdateAvailable);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        this.onControllerChange
      );
    }
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
  },
  methods: {
    onUpdateAvailable(event) {
      const registration = event.detail;
      this.waitingWorker = (registration && registration.waiting) || null;
      if (!this.waitingWorker) return;

      if (onUpdateAvailableAction({ hidden: document.hidden }) === "apply") {
        this.applyUpdate();
      } else {
        this.showSnackbar = true;
      }
    },
    onControllerChange() {
      this.controllerChanged = true;
      const action = onControllerChangeAction({
        reloading: this.reloading,
        hidden: document.hidden,
        updateRequested: this.updateRequested,
      });
      if (action === "reload") {
        this.reloadNow();
      } else if (action === "snackbar") {
        this.showSnackbar = true;
      }
    },
    onVisibilityChange() {
      if (!document.hidden) return;
      const action = onHiddenAction({
        reloading: this.reloading,
        controllerChanged: this.controllerChanged,
        waitingWorker: this.waitingWorker,
        updateRequested: this.updateRequested,
      });
      if (action === "reload") {
        this.reloadNow();
      } else if (action === "apply") {
        this.applyUpdate();
      }
    },
    onUpdateClick() {
      // Новый SW уже активен (другая вкладка активировала) → просто reload;
      // иначе — активируем, reload придёт по controllerchange.
      if (this.controllerChanged) {
        this.reloadNow();
      } else {
        this.applyUpdate();
      }
    },
    async applyUpdate() {
      this.showSnackbar = false;
      this.updateRequested = true;

      // Свежий registration.waiting надёжнее захваченного в updated(): пока
      // снекбар висел, мог приехать ещё более новый деплой — старая ссылка
      // стала redundant, и SKIP_WAITING в неё уходит в пустоту (клик «молчит»).
      let freshWaiting = null;
      try {
        const reg =
          navigator.serviceWorker &&
          (await navigator.serviceWorker.getRegistration());
        freshWaiting = (reg && reg.waiting) || null;
      } catch (e) {
        // getRegistration недоступен/упал — работаем с захваченным
      }
      const worker = resolveWorkerToSignal({
        freshWaiting,
        capturedWaiting: this.waitingWorker,
      });
      if (worker) {
        worker.postMessage({ type: "SKIP_WAITING" });
      }

      // Страховка: если controllerchange не придёт (воркер протух; страница ещё
      // не под контролем SW — первая сессия), перезагружаемся сами. reloadNow
      // за guard'ом, так что при нормальном controllerchange таймер — no-op.
      if (!this.fallbackTimer) {
        this.fallbackTimer = setTimeout(() => this.reloadNow(), FALLBACK_RELOAD_MS);
      }
    },
    reloadNow() {
      if (this.reloading) return;
      this.reloading = true;
      this.showSnackbar = false;
      window.location.reload();
    },
  },
};
</script>
